import {AfterViewInit, Directive, ElementRef, Inject, NgZone, OnDestroy, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {NavigationEnd, NavigationStart, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {MobilePlatformPolicyService} from '../../core/mobile/mobile-platform-policy.service';

/**
 * Compositor-only transition for explicitly marked Angular detail routes.
 * It never competes with the operating system's left-edge back gesture.
 */
@Directive({selector: '[appMobileStackTransition]'})
export class MobileStackTransitionDirective implements AfterViewInit, OnDestroy {
  private readonly isBrowser: boolean;
  private readonly subscriptions = new Subscription();
  private removeListeners: Array<() => void> = [];
  private currentRouteIsStack = false;
  private currentNavigationId = 0;
  private restoredNavigationId: number | null = null;
  private pointerId: number | null = null;
  private startX = 0;
  private startY = 0;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly router: Router,
    private readonly mobilePlatform: MobilePlatformPolicyService,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  public ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.subscriptions.add(this.router.events.pipe(
      filter((event): event is NavigationStart => event instanceof NavigationStart),
    ).subscribe((event) => {
      this.restoredNavigationId = event.restoredState?.navigationId ?? null;
    }));
    this.subscriptions.add(this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    ).subscribe((event) => this.onNavigationEnd(event)));

    this.zone.runOutsideAngular(() => {
      const element = this.host.nativeElement;
      const onPointerDown = (event: PointerEvent) => this.onPointerDown(event);
      const onPointerUp = (event: PointerEvent) => this.onPointerUp(event);
      const onPointerCancel = () => this.resetPointer();
      element.addEventListener('pointerdown', onPointerDown, {passive: true});
      element.addEventListener('pointerup', onPointerUp, {passive: true});
      element.addEventListener('pointercancel', onPointerCancel, {passive: true});
      this.removeListeners = [
        () => element.removeEventListener('pointerdown', onPointerDown),
        () => element.removeEventListener('pointerup', onPointerUp),
        () => element.removeEventListener('pointercancel', onPointerCancel),
      ];
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.removeListeners.forEach((remove) => remove());
  }

  private onNavigationEnd(event: NavigationEnd): void {
    const active = this.getActiveRouteData();
    const wasStack = this.currentRouteIsStack;
    this.currentRouteIsStack = active.mobileStack === true;
    const restored = this.restoredNavigationId;
    this.restoredNavigationId = null;
    const direction = restored !== null && restored < this.currentNavigationId ? 'back' : 'forward';
    this.currentNavigationId = event.id;
    // The destination may be a tab root without `mobileStack`.  Still animate
    // a browser/system back from a marked detail page to that root, but never
    // attach the custom swipe handler on the root itself.
    const returnsFromStackToRoot = wasStack && restored !== null;
    if (!this.mobilePlatform.isMobileExperience || (!this.currentRouteIsStack && !returnsFromStackToRoot)) {
      return;
    }
    const host = this.host.nativeElement;
    host.classList.remove('mobile-stack-transition--forward', 'mobile-stack-transition--back');
    // Force the previous class to settle before starting the next compositor animation.
    void host.offsetWidth;
    host.classList.add(`mobile-stack-transition--${direction}`);
    window.setTimeout(() => host.classList.remove(
      'mobile-stack-transition--forward',
      'mobile-stack-transition--back',
    ), 220);
  }

  private getActiveRouteData(): Record<string, unknown> {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.data;
  }

  private onPointerDown(event: PointerEvent): void {
    if (!this.currentRouteIsStack || !this.mobilePlatform.isMobileExperience || !event.isPrimary || event.pointerType !== 'touch') {
      return;
    }
    // Reserve the actual edge for the operating system's interactive-back gesture.
    if (event.clientX <= 24 || event.clientX > 88 || this.ignoresTarget(event.target)) {
      return;
    }
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
  }

  private onPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) {
      return;
    }
    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;
    this.resetPointer();
    if (deltaX < 48 || deltaX <= Math.abs(deltaY) * 1.15) {
      return;
    }
    this.zone.run(() => window.history.back());
  }

  private resetPointer(): void {
    this.pointerId = null;
    this.startX = 0;
    this.startY = 0;
  }

  private ignoresTarget(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest([
      'button', 'a', 'input', 'textarea', 'select', '[contenteditable="true"]',
      '.swiper', '.p-dialog', '.p-sidebar', 'video', 'audio', '[data-mobile-stack-swipe-ignore]',
    ].join(','));
  }
}
